import { addHours, differenceInHours } from "date-fns";
import memoizee from "memoizee";
import { determineRootObjectTypes } from "../../backend/helpers/configurationHelpers";
import { ViewType } from "../../backend/constants/viewTypes";
import { IHierarchyItem } from "src/backend/interfaces/IItem";
import { IViewConfigurationItem } from "src/backend/interfaces/configuration/IConfiguration";
import { IMoveOrResizeManyPayload } from "src/rematch/interfaces/payloads/itemReducers";



export const getLinkedTaskIdsRecursive = (task: IHierarchyItem, taskSet: Set<number>, allData: IHierarchyItem[]): Set<number> => {
  // Check if the new link would cause a circular dependency
  for (const linkedTaskId of task.linksTo) {
    if (taskSet.has(linkedTaskId)) {
      continue;
    }
    taskSet.add(linkedTaskId);
    getLinkedTaskIdsRecursive(allData[linkedTaskId], taskSet, allData);
  }
  return taskSet;
};

export const getChildrenMinimumDurationInHours = (data: IHierarchyItem[], task: IHierarchyItem): number => {
  let minimumDuration = 0;
  for (const childTaskId of task.children) {
    // TODO (minor optimisation): check if the childTask has already been accounted for
    // by a previous call to following graph traversal function.
    const childTask = data[childTaskId];
    const newMinimum = getTaskGraphMinimumSpanInHours(data, childTask);
    if (newMinimum > minimumDuration) {
      minimumDuration = newMinimum;
    }
  }
  return minimumDuration;
};

export const getTaskGraphMinimumSpanInHours = (data: IHierarchyItem[], task: IHierarchyItem): number => {
  // Returns the minimum duration to which all the tasks
  const taskSet = findAllTasksInDependencyGraph(data, task, new Set([task]));
  let minTotalDuration = 0;
  for (const currentTask of taskSet) {
    if (currentTask.linksFrom.length === 0) {
      const longestPathFromOriginTask = getLongestDependencyChainDurationForwardsInHours(data, currentTask);
      if (longestPathFromOriginTask > minTotalDuration) {
        minTotalDuration = longestPathFromOriginTask;
      }
    }
  }
  return minTotalDuration;
};

export const getLongestDependencyChainDurationForwardsInHours = (data: IHierarchyItem[], task: IHierarchyItem): number => {
  const ownDuration = differenceInHours(task.end, task.start);
  let dependentsMaxDuration = 0;
  for (const taskId of task.linksTo) {
    const linkedTask = data[taskId];
    const newDuration = getLongestDependencyChainDurationForwardsInHours(data, linkedTask);
    if (newDuration > dependentsMaxDuration) {
      dependentsMaxDuration = newDuration;
    }
  }
  return ownDuration + dependentsMaxDuration;
};

export const getLongestDependencyChainDurationBackwardsInHours = (data: IHierarchyItem[], task: IHierarchyItem): number => {
  const ownDuration = differenceInHours(task.end, task.start);
  let dependeesMaxDuration = 0;
  for (const taskId of task.linksFrom) {
    const linkedTask = data[taskId];
    const newDuration = getLongestDependencyChainDurationBackwardsInHours(data, linkedTask);
    if (newDuration > dependeesMaxDuration) {
      dependeesMaxDuration = newDuration;
    }
  }
  return ownDuration + dependeesMaxDuration;
};

export const findAllTasksInDependencyGraph = (data: IHierarchyItem[], task: IHierarchyItem, taskSet: Set<IHierarchyItem>): Set<IHierarchyItem> => {
  // Traverse the entire graph of tasks that are dependent on the starting task
  // or which the starting task is dependent on.
  for (const linkedTaskId of task.linksTo) {
    const linkedTask = data[linkedTaskId];
    if (!taskSet.has(linkedTask)) {
      taskSet.add(linkedTask);
      findAllTasksInDependencyGraph(data, linkedTask, taskSet);
    }
  }
  for (const linkedTaskId of task.linksFrom) {
    const linkedTask = data[linkedTaskId];
    if (!taskSet.has(linkedTask)) {
      taskSet.add(linkedTask);
      findAllTasksInDependencyGraph(data, linkedTask, taskSet);
    }
  }
  return taskSet;
};

export const getSubtasksRecursive = (data: IHierarchyItem[], task: IHierarchyItem, taskSet: Set<IHierarchyItem>): Set<IHierarchyItem> => {
  for (const childId of task.children) {
    const subtask = data[childId];
    if (taskSet.has(subtask)) {
      //Should not happen. A task should have only one parent and a circular hierarchy should not be possible.
      continue;
    }
    taskSet.add(subtask);
    getSubtasksRecursive(data, subtask, taskSet);
  }
  return taskSet;
};

export const updateUpdateDictWithLinkedTasks = (data: IHierarchyItem[], baseTask: IHierarchyItem, updateDict: IMoveOrResizeManyPayload, originTask = null): IMoveOrResizeManyPayload => {
  for (const linkedTaskId of baseTask.linksTo) {
    const linkedTask = data[linkedTaskId];
    if (linkedTask === originTask) {
      //We arrived from this node. Skip it.
      continue;
    }
    const linkedTaskGapDiffHours = differenceInHours(linkedTask.start, updateDict[baseTask.id].newEnd);
    if (linkedTaskGapDiffHours < 0) {
      // The new end time of the base task is greater than the start time of the dependent task.
      if (updateDict[linkedTask.id] && updateDict[linkedTask.id].newStart > addHours(linkedTask.start, -linkedTaskGapDiffHours)) {
        // The linked task is already about to be updated to a later time than what this check would require. No action required here.
        continue;
      }
      updateDict[linkedTask.id] = {
        itemId: linkedTask.id,
        newStart: addHours(linkedTask.start, -linkedTaskGapDiffHours),
        newEnd: addHours(linkedTask.end, -linkedTaskGapDiffHours),
      };
      updateUpdateDictWithLinkedTasks(data, linkedTask, updateDict, baseTask);
    }
  }

  //Apply the same in reverse for tasks linking to baseTask.
  for (const linkedTaskId of baseTask.linksFrom) {
    const fromLinkedTask = data[linkedTaskId];
    if (fromLinkedTask === originTask) {
      //We arrived from this node. Skip it.
      continue;
    }
    const linkedTaskGapDiffHours = differenceInHours(updateDict[baseTask.id].newStart, fromLinkedTask.end);
    if (linkedTaskGapDiffHours < 0) {
      // The new start time of the base task is lower than the end time of the linking.
      if (updateDict[fromLinkedTask.id] && updateDict[fromLinkedTask.id].newEnd < addHours(fromLinkedTask.start, linkedTaskGapDiffHours)) {
        // The fromlinked task is already about to be updated to an earlier time than what this check would require. No action required here.
        continue;
      }
      updateDict[fromLinkedTask.id] = {
        itemId: fromLinkedTask.id,
        newStart: addHours(fromLinkedTask.start, linkedTaskGapDiffHours),
        newEnd: addHours(fromLinkedTask.end, linkedTaskGapDiffHours),
      };
      updateUpdateDictWithLinkedTasks(data, fromLinkedTask, updateDict, baseTask);
    }
  }

  return updateDict;
};

export const getRootLevelData = memoizee(
  (viewConfiguration: IViewConfigurationItem, allData: IHierarchyItem[]) => {
    if (viewConfiguration.viewType === ViewType.Scheduling) {
      // It's ok to see to see items from other than actual root level of hierarchy.
      // Those items are without parent and user might want to assign them to some root level item.
      return Object.values(allData).filter((item) => item.hierarchyLevel === 0);
    }
    if (viewConfiguration.viewType === ViewType.Resourcing) {
      // Scheduling view should show only items from the actual root level of hierarchy
      const rootObjectTypeIds = determineRootObjectTypes(viewConfiguration);
      return Object.values(allData).filter((item) => item.hierarchyLevel === 0 && rootObjectTypeIds.includes(item.objectType));
    }
    return [];
  },
  { max: 10 } //This number should correspond with the maximum number of views active at the same time. 10 should be enough for everyone. ;)
);
